import React, { useEffect, useState } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';

const SESSION_KEY = 'wth_lightbox_dismissed_v1';

/**
 * Homepage promotional lightbox (modal). Fully admin-controlled via
 * /api/settings/public → lightbox.{enabled,image,headline,subhead,cta_label,cta_href,delay_ms}.
 * DESKTOP-ONLY: hidden on viewports < md (768 px) via Tailwind's `hidden md:flex`.
 * Auto-dismissed once per session (sessionStorage) so users see it at most once per visit.
 */
const Lightbox = () => {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/settings/public').then(r => {
      if (!alive) return;
      const lb = r.data?.lightbox;
      if (!lb || !lb.enabled) return;
      // Skip on mobile viewports (extra guard beyond the CSS media query)
      if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches) return;
      try {
        if (sessionStorage.getItem(SESSION_KEY) === '1') return;
      } catch { /* noop */ }
      setConfig(lb);
      const delay = Math.max(0, Math.min(30000, parseInt(lb.delay_ms || 2000, 10)));
      const t = setTimeout(() => alive && setOpen(true), delay);
      return () => clearTimeout(t);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const close = () => {
    setOpen(false);
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* noop */ }
  };

  if (!config || !open) return null;

  return (
    <div className="fixed inset-0 z-[80] hidden md:flex items-center justify-center px-6" data-testid="homepage-lightbox">
      <div onClick={close} className="absolute inset-0 bg-[#213A60]/70 backdrop-blur-sm animate-in fade-in duration-300" />
      <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-3xl w-full grid md:grid-cols-2 animate-in zoom-in-95 duration-300">
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-white text-[#213A60] flex items-center justify-center shadow-lg transition-colors"
          data-testid="lightbox-close"
        >
          <X className="w-4 h-4" />
        </button>
        {/* Image side */}
        <div className="relative aspect-square md:aspect-auto bg-[#F8F4EC]">
          {config.image ? (
            <img src={config.image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs uppercase tracking-widest">No image</div>
          )}
        </div>
        {/* Copy side */}
        <div className="p-8 md:p-10 flex flex-col justify-center bg-[#F8F4EC]">
          {config.headline && (
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#213A60] leading-tight">{config.headline}</h2>
          )}
          {config.subhead && (
            <p className="mt-3 text-sm text-[#333333] leading-relaxed">{config.subhead}</p>
          )}
          {config.cta_label && (
            <a
              href={config.cta_href || '/shop'}
              onClick={close}
              className="mt-6 inline-flex items-center gap-2 self-start bg-[#D4A63A] hover:bg-[#B8912F] text-[#213A60] font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-[0.2em] transition-colors"
              data-testid="lightbox-cta"
            >
              {config.cta_label}
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
